import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  SafeAreaView,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import OrnamentBackground from '../../components/OrnamentBackground';
import { API_URL } from '../../config';

import { createLogger } from '../../utils/logger';
const logger = createLogger('ResetPassword');

type RouteParams = {
  identifier: string;
  verificationCode: string;
};

const ResetPasswordScreen = ({
  navigation,
  route,
}: {
  navigation: NavigationProp<any>;
  route: RouteProp<any, any>;
}) => {
  const params = route.params as RouteParams | undefined;
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const validatePassword = (password: string): boolean => {
    const hasMinLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
  };

  const handleResetPassword = async () => {
    if (isLoading) return;

    // Reset any previous errors
    setError(null);

    // Validate password requirements
    if (!validatePassword(newPassword)) {
      setError('Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character.');
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: params?.identifier,
          code: params?.verificationCode,
          newPassword: newPassword
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        Alert.alert(
          'Success',
          'Your password has been reset successfully.',
          [
            {
              text: 'OK',
              onPress: () => navigation.navigate('Login')
            }
          ]
        );
      } else {
        setError(result.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      logger.error('Reset password error:', error);
      setError('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />

      <View style={styles.topSection}>
        <Image source={require('../../assets/logo.png')} style={styles.logo} />
        <Text style={styles.welcomeText}>Reset Password</Text>
        <Text style={styles.description}>
          Please enter your new password. Make sure it meets all the requirements to keep your account secure.
        </Text>
      </View>

      <View style={styles.mainContainer}>
        {error && (
          <View style={styles.errorContainer}>
            <View style={styles.errorContent}>
              <Text style={styles.errorTitle}>Password Requirements Not Met</Text>
              <Text style={styles.errorMessage}>{error}</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setError(null)}
              >
                <Text style={styles.closeButtonText}>×</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <View style={styles.inputContainer}>
          <ScrollView style={styles.scrollView}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>New Password</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter your new password"
                value={newPassword}
                onChangeText={setNewPassword}
                secureTextEntry={!showPassword}
                autoCapitalize="none"
                placeholderTextColor="#A9A9A9"
              />
              <Text style={styles.inputHint}>
                Password must contain at least 8 characters, including uppercase, lowercase, number, and special character.
              </Text>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Confirm Password</Text>
              <TextInput
                style={styles.input}
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                secureTextEntry={!showPassword}
                autoCapitalize="none"
                placeholderTextColor="#A9A9A9"
              />
            </View>

            <TouchableOpacity
              style={styles.showPasswordContainer}
              onPress={() => setShowPassword(!showPassword)}
            >
              <Text style={styles.showPasswordText}>
                {showPassword ? 'Hide' : 'Show'} Password
              </Text>
            </TouchableOpacity>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[
                  styles.resetButton,
                  {
                    opacity:
                      newPassword.length > 0 && confirmPassword.length > 0 && !isLoading ? 1 : 0.6,
                  },
                ]}
                onPress={handleResetPassword}
                disabled={!newPassword || !confirmPassword || isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.resetButtonText}>Reset Password</Text>
                )}
              </TouchableOpacity>
            </View>

            <View style={styles.bottomPadding} />
          </ScrollView>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
    flex: 0.58,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  welcomeText: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: ms(20),
    paddingHorizontal: ms(40),
  },
  mainContainer: {
    flex: 0.88,
    position: 'relative',
  },
  errorContainer: {
    backgroundColor: '#E85C5C',
    paddingVertical: ms(25),
    paddingHorizontal: ms(20),
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    marginBottom: ms(-20),
    position: 'relative',
    zIndex: 1,
  },
  errorContent: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  errorTitle: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    fontWeight: 'bold',
    marginBottom: ms(4),
  },
  errorMessage: {
    fontSize: ms(14, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    opacity: 0.8,
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    padding: ms(5),
  },
  closeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(30),
    position: 'relative',
    zIndex: 2,
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  inputGroup: {
    marginBottom: ms(24),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  inputHint: {
    fontSize: ms(12, 0.3),
    color: '#A9A9A9',
    marginTop: ms(4),
  },
  showPasswordContainer: {
    alignItems: 'flex-end',
    marginBottom: ms(20),
  },
  showPasswordText: {
    color: '#29BFC0',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  resetButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  resetButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(30),
  },
});

export default ResetPasswordScreen;
